import { createContext, useContext, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useVerticalModule } from '@lipaflow/shared'
import {
  getPortalContext,
  NEUTRAL_PORTAL_CONTEXT,
  type PortalContextResponse,
} from '../services/portalContext'

interface PortalVerticalValue extends PortalContextResponse {
  /**
   * Is a feature enabled for this tenant? The key is a string the vertical
   * declares — Tier 1 never learns what it means (ADR-0004 §3). Unknown keys
   * default to enabled, so a vertical's own pages show unless the tenant has
   * explicitly turned them off.
   */
  isFeatureEnabled: (feature: string) => boolean
  isLoading: boolean
}

const PortalVerticalContext = createContext<PortalVerticalValue>({
  ...NEUTRAL_PORTAL_CONTEXT,
  isFeatureEnabled: () => true,
  isLoading: false,
})

interface PortalVerticalProviderProps {
  children: ReactNode
  /**
   * Fallback context while loading or when /portal/context is unavailable.
   * Defaults to the provided vertical module's own default, so a shell that
   * composes `@lipaflow/education` gets school labels without Tier 1 knowing
   * what a school is.
   */
  defaultContext?: PortalContextResponse
}

/**
 * Fetches the tenant's vertical context once per session and exposes it to
 * every authenticated portal page.
 *
 * Two channels, kept separate (ADR-0004 §1): this is *context* — per tenant,
 * resolved at runtime, carrying labels and feature flags. Routes, nav and
 * slots are the *module* — per deployment, resolved at compile time.
 */
export function PortalVerticalProvider({
  children,
  defaultContext,
}: PortalVerticalProviderProps) {
  const module = useVerticalModule<PortalContextResponse>()
  const fallback = defaultContext ?? module?.defaultContext ?? NEUTRAL_PORTAL_CONTEXT

  const { data, isLoading } = useQuery<PortalContextResponse>({
    queryKey: ['portal-context'],
    queryFn: getPortalContext,
    staleTime: Infinity,
    retry: 1,
  })

  const context = data ?? fallback
  const value: PortalVerticalValue = {
    ...context,
    isFeatureEnabled: (feature: string) => context.features[feature] !== false,
    isLoading,
  }

  return (
    <PortalVerticalContext.Provider value={value}>
      {children}
    </PortalVerticalContext.Provider>
  )
}

export function usePortalVertical(): PortalVerticalValue {
  return useContext(PortalVerticalContext)
}
